/**
 * FIELD QUILT — the ground the stage is actually made of.
 *
 * The level tour exposed the real problem with Home Field: roughly 70% of
 * every frame was one undifferentiated green plane with a road and a river
 * drawn on it. Adding barns did not fix that, because the barns were never
 * the problem — the space between them was.
 *
 * Farmland seen from above is a QUILT. Parcels of different crops at different
 * stages, rows running whichever way each field was worked, hedgerows and
 * treelines on the boundaries, dirt tracks connecting them. The parcel edges
 * are what the eye reads first, long before it resolves any single crop.
 *
 * HOW IT IS BUILT. One merged mesh per crop, spanning exactly one terrain
 * wrap, scrolled by the same mechanism as the river and the road. Parcels are
 * authored in a deterministic walk rather than scattered, because a farm is a
 * surveyed thing — random rectangles read as noise, and the regularity broken
 * by the river is precisely what makes the river read as a river.
 *
 * ROW DIRECTION comes free: the crop-row texture stripes along U, so a parcel
 * picks its direction purely by which axis it maps U to. Neighbouring parcels
 * with crossed rows are most of why real farmland looks like a quilt.
 */

import * as THREE from 'three'
import { createCropRowTexture } from '../assets/propTextures'

/** A crop as it reads from 800 feet in September light. */
interface Crop {
  color: string
  /** World units per row-texture repeat. Small = fine corduroy. */
  rowSpacing: number
  roughness: number
}

/**
 * Virginia in early autumn. The palette has to hold together as a single
 * landscape, so every entry sits in the same value band — variety comes from
 * hue and row density, not from one parcel being twice as bright as its
 * neighbour. A field that pops out of the quilt reads as a bug.
 */
const CROPS: Crop[] = [
  { color: '#5c7038', rowSpacing: 2.4, roughness: 0.95 }, // standing corn
  { color: '#43552a', rowSpacing: 2.0, roughness: 0.96 }, // mature corn, darker
  { color: '#6a7c3f', rowSpacing: 3.1, roughness: 0.95 }, // soybean
  { color: '#8d7c45', rowSpacing: 2.7, roughness: 0.93 }, // wheat stubble
  { color: '#9a8b52', rowSpacing: 4.2, roughness: 0.92 }, // cut hay, wide swaths
  { color: '#574433', rowSpacing: 2.2, roughness: 0.97 }, // turned earth
  { color: '#65723f', rowSpacing: 6.0, roughness: 0.95 }, // rough pasture
]

/** Hedgerow and treeline. Nearly black-green so boundaries read as edges. */
const HEDGE_COLOR = '#2b3720'
/** Farm track — pale dry dirt, the only warm line in the quilt. */
const TRACK_COLOR = '#7a6f57'

/** Deterministic hash → [0,1). Same layout every run, no seeded-RNG plumbing. */
function hash(n: number): number {
  let v = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b)
  v = Math.imul(v ^ (v >>> 13), 0xc2b2ae35)
  return ((v ^ (v >>> 16)) >>> 0) / 4294967296
}

interface Parcel {
  x0: number
  x1: number
  y0: number
  y1: number
  crop: number
  /** True when rows run north–south (stripes vary across X). */
  rowsNorthSouth: boolean
}

export class FieldQuilt {
  readonly group = new THREE.Group()
  /** Every mesh that scrolls, so the owner can wrap them as one. */
  readonly segments: THREE.Mesh[] = []

  private readonly rowTexture = createCropRowTexture()
  private readonly materials: THREE.MeshStandardMaterial[] = []
  private readonly hedgeMaterial = new THREE.MeshStandardMaterial({
    color: HEDGE_COLOR,
    roughness: 0.98,
    metalness: 0,
  })
  private readonly trackMaterial = new THREE.MeshStandardMaterial({
    color: TRACK_COLOR,
    roughness: 0.96,
    metalness: 0,
  })

  /**
   * @param wrap        terrain repeat distance; the quilt spans exactly this
   * @param blocked     x-range the parcels must keep clear at a given y
   *                    (the river and road corridors), or null for open ground
   */
  constructor(
    wrap: number,
    blocked: (y: number) => Array<[number, number]>,
  ) {
    this.group.name = 'FieldQuilt'

    for (const crop of CROPS) {
      const tex = this.rowTexture.clone()
      tex.needsUpdate = true
      this.materials.push(
        new THREE.MeshStandardMaterial({
          color: crop.color,
          map: tex,
          roughness: crop.roughness,
          metalness: 0,
        }),
      )
    }

    // THREE PERIODS, NOT ONE.
    //
    // The scroll wraps a mesh by adding `wrap` to its position once it falls
    // off the bottom, so a mesh spanning exactly one period covers the visible
    // band at some phases and leaves a hard edge across the screen at others —
    // the quilt visibly stopped mid-field and bare ground continued past it.
    //
    // The layout is periodic, so copies offset by ±wrap tile it seamlessly and
    // the span is then wide enough to cover the view at every phase. The cost
    // is a few hundred extra quads, which is nothing.
    const period = layOutParcels(wrap, blocked)
    const parcels: Parcel[] = []
    for (const shift of [-wrap, 0, wrap]) {
      for (const p of period) parcels.push({ ...p, y0: p.y0 + shift, y1: p.y1 + shift })
    }

    // One merged mesh per crop. Parcels never move relative to each other, so
    // there is nothing to gain from instancing and a lot to gain from having
    // per-parcel UVs — which is what carries the row direction.
    for (let c = 0; c < CROPS.length; c += 1) {
      const mine = parcels.filter((p) => p.crop === c)
      if (mine.length === 0) continue
      const geo = quadsGeometry(mine, CROPS[c].rowSpacing)
      const mesh = new THREE.Mesh(geo, this.materials[c])
      mesh.position.z = -2.35
      mesh.receiveShadow = true
      this.segments.push(mesh)
      this.group.add(mesh)
    }

    // Boundaries. Drawn as their own strips rather than baked into the parcel
    // texture so they survive at any parcel size and read as planted rather
    // than as an outline.
    const hedges: Parcel[] = []
    const tracks: Parcel[] = []
    for (let i = 0; i < parcels.length; i += 1) {
      const p = parcels[i]
      const r = hash(i * 977 + 13)
      // Most boundaries get a hedgerow; a few get a dirt track instead, and a
      // few get nothing, because a perfectly outlined grid reads as a diagram.
      if (r < 0.62) {
        hedges.push({ ...p, y0: p.y1 - 0.55, rowsNorthSouth: false })
      } else if (r < 0.78) {
        tracks.push({ ...p, y0: p.y1 - 0.9, rowsNorthSouth: false })
      }
      const r2 = hash(i * 613 + 91)
      if (r2 < 0.5) {
        hedges.push({ ...p, x0: p.x1 - 0.5, rowsNorthSouth: true })
      }
    }
    for (const [list, mat] of [
      [hedges, this.hedgeMaterial],
      [tracks, this.trackMaterial],
    ] as const) {
      if (list.length === 0) continue
      const mesh = new THREE.Mesh(quadsGeometry(list, 4), mat)
      mesh.position.z = -2.32
      this.segments.push(mesh)
      this.group.add(mesh)
    }
  }

  dispose(): void {
    this.rowTexture.dispose()
    for (const m of this.materials) {
      m.map?.dispose()
      m.dispose()
    }
    this.hedgeMaterial.dispose()
    this.trackMaterial.dispose()
    for (const s of this.segments) s.geometry.dispose()
  }
}

/**
 * Lay the quilt out COLUMN-MAJOR: divide x into strips, then subdivide each
 * strip in y independently.
 *
 * WHY NOT ROW-MAJOR. The first version walked the wrap in horizontal bands and
 * split each band into parcels. That put a field boundary at the SAME y right
 * across the frame, over and over — and a horizontal line spanning the whole
 * screen, repeating, is exactly the visual signature of a conveyor belt. The
 * stage read as "a repeating roll" because structurally it was one.
 *
 * Column-major staggers every boundary. No horizontal edge spans more than one
 * strip, so the eye finds no shared rhythm to lock onto, which is also how real
 * farmland looks from the air: neighbours do not agree on where a field ends.
 */
function layOutParcels(
  wrap: number,
  blocked: (y: number) => Array<[number, number]>,
): Parcel[] {
  const out: Parcel[] = []
  const X_MIN = -34
  const X_MAX = 34

  let x = X_MIN
  let col = 0
  while (x < X_MAX) {
    let width = 8 + hash(col * 101 + 3) * 13
    if (x + width > X_MAX - 4) width = X_MAX - x
    const xEnd = x + width

    // Subdivide this strip in y. Fields are collected before being emitted so
    // the pair straddling the wrap seam can be harmonised below.
    const fields: Array<{ y0: number; y1: number; crop: number; rows: boolean }> = []
    let y = -wrap / 2
    let row = 0
    while (y < wrap / 2) {
      const depth = 11 + hash(col * 733 + row * 31 + 7) * 20
      const yTop = Math.min(wrap / 2, y + depth)
      if (yTop - y < 3) break
      fields.push({
        y0: y,
        y1: yTop,
        crop: Math.floor(hash(col * 7919 + row * 131 + 17) * CROPS.length),
        rows: hash(col * 271 + row * 53 + 5) < 0.55,
      })
      y = yTop
      row += 1
    }

    // HIDE THE WRAP SEAM. Every column has to close at ±wrap/2, so without
    // this there is one field boundary at the same y in every single column —
    // a full-width line across the frame once per lap, which is the exact
    // artefact the column-major layout exists to remove.
    //
    // Giving the first and last field of each column the same crop and row
    // direction makes the two abutting parcels read as ONE field spanning the
    // seam, so the boundary disappears rather than merely moving.
    if (fields.length > 1) {
      fields[0].crop = fields[fields.length - 1].crop
      fields[0].rows = fields[fields.length - 1].rows
    }

    for (const f of fields) {
      // Corridors are sampled at three depths and unioned, so a parcel never
      // clips the river where it curves through this field.
      const bars = [...blocked(f.y0), ...blocked((f.y0 + f.y1) / 2), ...blocked(f.y1)]
      // A parcel surviving on both sides of a corridor becomes two parcels —
      // which is what happens on the ground when a road cuts a field.
      for (const piece of clip(x, xEnd, bars)) {
        if (piece[1] - piece[0] < 2.5) continue
        out.push({
          x0: piece[0],
          x1: piece[1],
          y0: f.y0,
          y1: f.y1,
          crop: f.crop,
          rowsNorthSouth: f.rows,
        })
      }
    }

    x = xEnd
    col += 1
  }
  return out
}

/** Subtract every blocked span from [a,b], returning the surviving pieces. */
function clip(a: number, b: number, bars: Array<[number, number]>): Array<[number, number]> {
  let pieces: Array<[number, number]> = [[a, b]]
  for (const [lo, hi] of bars) {
    const next: Array<[number, number]> = []
    for (const [s, e] of pieces) {
      if (hi <= s || lo >= e) {
        next.push([s, e])
        continue
      }
      if (lo > s) next.push([s, lo])
      if (hi < e) next.push([hi, e])
    }
    pieces = next
  }
  return pieces
}

/**
 * Merge parcels into one geometry, assigning UVs per parcel so the shared row
 * texture runs along whichever axis that parcel was worked.
 *
 * UVs are derived from WORLD position, not from parcel-local coordinates, so
 * adjacent parcels sharing a row direction stay in phase across their shared
 * edge — the rows continue rather than jumping, which is what happens when a
 * farmer works two fields the same way.
 */
function quadsGeometry(parcels: Parcel[], rowSpacing: number): THREE.BufferGeometry {
  const pos = new Float32Array(parcels.length * 12)
  const uv = new Float32Array(parcels.length * 8)
  const idx = new Uint32Array(parcels.length * 6)

  for (let i = 0; i < parcels.length; i += 1) {
    const p = parcels[i]
    const v = i * 12
    pos[v + 0] = p.x0; pos[v + 1] = p.y0; pos[v + 2] = 0
    pos[v + 3] = p.x1; pos[v + 4] = p.y0; pos[v + 5] = 0
    pos[v + 6] = p.x1; pos[v + 7] = p.y1; pos[v + 8] = 0
    pos[v + 9] = p.x0; pos[v + 10] = p.y1; pos[v + 11] = 0

    // Stripes run along U. Mapping U to X gives north–south rows; mapping U
    // to Y gives east–west rows. That single swap is the whole quilt effect.
    const u0 = (p.rowsNorthSouth ? p.x0 : p.y0) / rowSpacing
    const u1 = (p.rowsNorthSouth ? p.x1 : p.y1) / rowSpacing
    const w0 = (p.rowsNorthSouth ? p.y0 : p.x0) / (rowSpacing * 4)
    const w1 = (p.rowsNorthSouth ? p.y1 : p.x1) / (rowSpacing * 4)
    const t = i * 8
    if (p.rowsNorthSouth) {
      uv[t + 0] = u0; uv[t + 1] = w0
      uv[t + 2] = u1; uv[t + 3] = w0
      uv[t + 4] = u1; uv[t + 5] = w1
      uv[t + 6] = u0; uv[t + 7] = w1
    } else {
      uv[t + 0] = w0; uv[t + 1] = u0
      uv[t + 2] = w1; uv[t + 3] = u0
      uv[t + 4] = w1; uv[t + 5] = u1
      uv[t + 6] = w0; uv[t + 7] = u1
    }

    const o = i * 4
    const f = i * 6
    idx[f + 0] = o; idx[f + 1] = o + 1; idx[f + 2] = o + 2
    idx[f + 3] = o; idx[f + 4] = o + 2; idx[f + 5] = o + 3
  }

  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
  geo.setIndex(new THREE.BufferAttribute(idx, 1))
  geo.computeVertexNormals()
  return geo
}
